import { useState, type FormEvent } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/daf7n2ptto0ceqqnh780";

const channels = [
  { icon: "ri-mail-line", label: "邮箱", value: "hello@chiguaheji.example" },
  { icon: "ri-wechat-line", label: "微信", value: "chigua-hj (编辑部)" },
  { icon: "ri-time-line", label: "答疑时间", value: "工作日 10:00 - 18:00" },
];

export default function Contact() {
  const [formStatus, setFormStatus] = useState<
    { type: "success" | "error"; msg: string } | undefined
  >(undefined);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾 honeypot：若被自动填充，则不真正提交
    const hp = String(fd.get("website_alt") || "").trim();
    if (hp) {
      setFormStatus({ type: "success", msg: "感谢你的留言，我们已收到并会尽快回复。" });
      form.reset();
      return;
    }
    fd.delete("website_alt");

    setSubmitting(true);
    setFormStatus(undefined);
    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        body: fd,
      });
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta as Record<string, unknown>) || {};
      const code = parsed?.code as string;
      const serverMsg =
        (meta.message as string) ||
        (parsed?.message as string) ||
        (meta.detail as string) ||
        "";

      const ok = res.ok && code === "OK";
      const looksSpam = /spam|form data is spam/i.test(text);
      if (!ok || looksSpam) {
        setFormStatus({
          type: "error",
          msg: serverMsg || "提交失败，请稍后重试。",
        });
        return;
      }
      setFormStatus({ type: "success", msg: "感谢你的留言，我们已收到并会尽快回复。" });
      form.reset();
    } catch {
      setFormStatus({ type: "error", msg: "网络似乎开小差了，请稍后再试。" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="contact" className="scroll-mt-20 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          {/* 左：联系方式 */}
          <div className="flex flex-col gap-4 lg:col-span-2">
            <div className="rounded-2xl border border-background-200 bg-background-100 p-5 md:p-6">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
                <i className="ri-chat-3-line" aria-hidden="true"></i> 联系我们
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                投稿线索 · 意见建议
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                有正在追踪的话题想投稿，或对整理方式有建议？填写右侧表单告诉我们，
                编辑部会认真评估每条线索。
              </p>
              <div className="mt-5 flex flex-col gap-3">
                {channels.map((c) => (
                  <div
                    key={c.label}
                    className="flex items-center gap-3 rounded-xl bg-background-50 px-4 py-3"
                  >
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-secondary-100 text-lg text-secondary-700">
                      <i className={c.icon} aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">{c.label}</p>
                      <p className="text-sm font-medium text-foreground-900">{c.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-accent-200 bg-accent-100 p-5">
              <p className="text-sm font-semibold text-accent-900">投稿提示</p>
              <p className="mt-2 text-xs leading-relaxed text-accent-900/80">
                投稿时请尽量提供事件发生的时间与可查证的出处，方便编辑部快速评估并整理成可靠的时间线。
              </p>
            </div>
          </div>

          {/* 右：表单 */}
          <div className="rounded-2xl border border-background-200 bg-background-50 p-5 md:p-7 lg:col-span-3">
            <h3 className="font-heading text-lg font-bold text-foreground-950">填写联系反馈</h3>
            <p className="mt-1 text-sm text-foreground-500">
              我们会在 1-2 个工作日内回复。
            </p>

            <form id="chigua-contact-form" data-readdy-form="chigua-contact" className="mt-6 flex flex-col gap-4" onSubmit={handleSubmit} noValidate={false}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    你的称呼
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    联系邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  留言类型
                </label>
                <select
                  id="topic"
                  name="topic"
                  className="w-full cursor-pointer rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                >
                  <option value="投稿线索">投稿线索</option>
                  <option value="意见建议">意见建议</option>
                  <option value="合作咨询">合作咨询</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请描述你关注的瓜或想反馈的内容，尽量附上时间与出处……"
                  className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                ></textarea>
                <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
              </div>

              {/* 反垃圾 honeypot */}
              <input
                className="hp-field"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {formStatus && (
                <div
                  className={`rounded-lg px-4 py-3 text-sm ${
                    formStatus.type === "success"
                      ? "bg-secondary-100 text-secondary-900"
                      : "bg-primary-100 text-primary-800"
                  }`}
                  role={formStatus.type === "error" ? "alert" : "status"}
                >
                  {formStatus.msg}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i> 提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" aria-hidden="true"></i> 提交留言
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}